import {useEffect, useRef, useState} from 'react'
import {subscribe, unsubscribe} from '@/script/event.mjs'

export default ({
    differences,
    leftMap,
    rightMap,
    selectFirstDifference,
}) => {
    const [currentDifference, setCurrentDifference] = useState(0)
    const refLockSelectNode = useRef(false)

    useEffect(
        () => {
            if (! (leftMap && rightMap && 0 < differences.length)) {
                return
            }

            for (const [flag, cidL, cidR] of differences) {
                ({
                    ADD: () => {
                        const nodeR = rightMap.getNodeByCmpId(cidR)
                        rightMap.setNodeDiff(nodeR.id, ['A'])
                    },

                    DEL: () => {
                        const nodeL = leftMap.getNodeByCmpId(cidL)
                        leftMap.setNodeDiff(nodeL.id, ['D'])
                    },

                    UPT: () => {
                        const nodeL = leftMap.getNodeByCmpId(cidL)
                        const nodeR = rightMap.getNodeByCmpId(cidR)
                        const mods = leftMap.compareNode(nodeL, nodeR)
                        leftMap.setNodeDiff(nodeL.id, ['M', mods])
                        rightMap.setNodeDiff(nodeR.id, ['M', mods])
                    },
                })[flag]()
            }

            if (selectFirstDifference) {
                selectDiff(0)
            }

            const handleSelectNodes = (srcMap, dstMap) => {
                return async (selectedNodes) => {
                    if (
                        1 !== selectedNodes.size ||
                        refLockSelectNode.current
                    ) {
                        return
                    }

                    const [node] = selectedNodes
                    const cid = srcMap.getCmpIdByNodeId(node.id)

                    if (! cid) {
                        return
                    }

                    const n = dstMap.getNodeByCmpId(cid)

                    if (n) {
                        refLockSelectNode.current = true
                        await dstMap.execute(() => dstMap.selectNodes([n]))
                        refLockSelectNode.current = false
                    }
                }
            }

            const handleSelectNodesL = handleSelectNodes(leftMap, rightMap)
            const handleSelectNodesR = handleSelectNodes(rightMap, leftMap)
            subscribe(leftMap, 'selected_nodes_change', handleSelectNodesL)
            subscribe(rightMap, 'selected_nodes_change', handleSelectNodesR)

            return () => {
                unsubscribe(leftMap, 'selected_nodes_change', handleSelectNodesL)
                unsubscribe(rightMap, 'selected_nodes_change', handleSelectNodesR)
            }
        },

        [differences, leftMap, rightMap]
    )

    const selectDiff = async (diffIndex) => {
        const [type, idL, idR] = differences[diffIndex]
        const getLCmpId = node => leftMap.getCmpIdByNodeId(node.id)
        const getLNode = cmpId => leftMap.getNodeByCmpId(cmpId)
        const getRCmpId = node => rightMap.getCmpIdByNodeId(node.id)
        const getRNode = cmpId => rightMap.getNodeByCmpId(cmpId)

        const [nodeL, nodeR] = ({
            ADD: () => {
                const nodeR = getRNode(idR)
                const pcid = getRCmpId(nodeR.parent)
                const nodeL = getLNode(pcid)
                nodeL.isFolded = false
                return [nodeL, nodeR]
            },

            DEL: () => {
                const nodeL = getLNode(idL)
                const pcid = getLCmpId(nodeL.parent)
                const nodeR = getRNode(pcid)
                nodeR.isFolded = false
                return [nodeL, nodeR]
            },

            UPT: () => [
                getLNode(idL),
                getRNode(idR),
            ],
        }[type])()

        refLockSelectNode.current = true

        await Promise.all([
            leftMap.execute(() => leftMap.selectNodes([nodeL])),
            rightMap.execute(() => rightMap.selectNodes([nodeR])),
        ])

        refLockSelectNode.current = false
        setCurrentDifference(diffIndex)
    }

    const nextDiff = async () => {
        if (0 === differences.length) {
            return
        }

        if (currentDifference + 1 < differences.length) {
            await selectDiff(currentDifference + 1)
        }
        else {
            await selectDiff(0)
        }
    }

    const prevDiff = async () => {
        if (0 === differences.length) {
            return
        }

        if (0 < currentDifference) {
            await selectDiff(currentDifference - 1)
        }
        else {
            await selectDiff(differences.length - 1)
        }
    }

    return {
        currentDifference,
        differences,
        leftMap,
        nextDiff,
        prevDiff,
        rightMap,
        selectDiff,
    }
}
